/**
 * TREN 1.2 / corrección 2026-09-11 — BLOQUEO DE PANTALLA de la sesión de piso.
 *
 * 🔴 BUG #493 (causa raíz de "re-PIN al abrir mesa" y "Mis tickets en blanco"):
 * el provider creaba el contexto pero NUNCA renderizaba <Ctx.Provider> — todos
 * los consumidores (useFloorActing) recibían el DEFAULT (token()=>null) y cada
 * vista caía a su camino viejo de PIN propio o se quedaba sin identidad.
 * REGLA fijada: el acting token del DESBLOQUEO es la ÚNICA fuente de identidad
 * de piso. El PIN se pide en EXACTAMENTE dos lugares: desbloquear la pantalla y
 * "Cambiar mesero". Ninguna acción con token vigente vuelve a pedir PIN.
 *
 * Lock: al cargar · tras N s sin actividad (floor_settings.lock_idle_seconds,
 * default 180 — decisión Arturo 11/09) · botón "Bloquear". NUNCA se auto-bloquea
 * con un candado retenido (holdLock: modal abierto o comanda capturada sin
 * enviar — perder captura por un lock es inaceptable). Los hijos se DESMONTAN
 * al bloquear (nada se ve sin PIN). Sesiones no-piso: no-op.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, setFloorActingToken } from '../lib/api';
import { FloorPinModal, FloorPinResult } from './FloorPinModal';
import { BUILD_ID } from '../buildInfo';

const ACTING_TTL_MS = 5 * 60 * 1000; // espejo del backend (acting token 5 min)

export interface FloorActing { token: string; name: string; userId: string; exp: number }

interface FloorCtx {
  isFloor: boolean;
  /** token vigente o null (vencido/bloqueado). */
  token: () => string | null;
  /** nombre del mesero que desbloqueó (para atribución visible). */
  actingName: () => string | null;
  /** userId del mesero del PIN (autoría visible; p. ej. aviso de mesa ajena). */
  actingUserId: () => string | null;
  /** adopta el resultado de un pin-verify (p. ej. cambiar mesero). */
  adopt: (r: FloorPinResult) => void;
  /** bloquea la pantalla ya. */
  lock: () => void;
  /** retén/suelta el candado del auto-lock (modal abierto / captura sin enviar). */
  holdLock: (on: boolean) => void;
}

const Ctx = createContext<FloorCtx>({
  isFloor: false, token: () => null, actingName: () => null, actingUserId: () => null,
  adopt: () => {}, lock: () => {}, holdLock: () => {},
});

export const useFloorActing = () => useContext(Ctx);

export function FloorLockProvider({ me, children, initialActing = null }: {
  me?: any;
  children: any;
  /** SOLO PRUEBAS (SSR no puede desbloquear): estado inicial del acting. */
  initialActing?: FloorActing | null;
}) {
  const isFloor = !!me?.isFloor;
  const [acting, setActing] = useState<FloorActing | null>(initialActing);
  const [idleSecs, setIdleSecs] = useState(180);
  const lastActivity = useRef(Date.now());
  const holds = useRef(0);

  // Config del lock (default 180 s si el fetch falla — fail-locked, no fail-open).
  useEffect(() => {
    if (!isFloor) return;
    api('/pos/floor/settings')
      .then((r: any) => setIdleSecs(Number(r?.lockIdleSeconds) || 180))
      .catch(() => setIdleSecs(180));
  }, [isFloor]);

  // Actividad del usuario (toques/teclas — la CAPTURA cuenta): resetea el reloj.
  useEffect(() => {
    if (!isFloor) return;
    const mark = () => { lastActivity.current = Date.now(); };
    const evs: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, mark));
  }, [isFloor]);

  // Vigilante: bloquea por inactividad o token vencido — NUNCA con candado
  // retenido (modal abierto / comanda con renglones sin enviar).
  useEffect(() => {
    if (!isFloor || !acting) return;
    const iv = window.setInterval(() => {
      if (holds.current > 0) return;
      const now = Date.now();
      if (now >= acting.exp || now - lastActivity.current >= idleSecs * 1000) {
        setFloorActingToken(null); // el header muere junto con la pantalla
        setActing(null);
      }
    }, 1000);
    return () => window.clearInterval(iv);
  }, [isFloor, acting, idleSecs]);

  const adopt = useCallback((r: FloorPinResult) => {
    lastActivity.current = Date.now();
    setFloorActingToken(r.actingToken); // header X-Floor-Acting en TODOS los requests
    setActing({ token: r.actingToken, name: r.name, userId: r.userId, exp: Date.now() + ACTING_TTL_MS });
  }, []);
  const lock = useCallback(() => { holds.current = 0; setFloorActingToken(null); setActing(null); }, []);
  const holdLock = useCallback((on: boolean) => {
    holds.current = Math.max(0, holds.current + (on ? 1 : -1));
  }, []);
  const token = useCallback(
    () => (acting && Date.now() < acting.exp ? acting.token : null),
    [acting],
  );
  const actingName = useCallback(() => (acting ? acting.name : null), [acting]);
  const actingUid = useCallback(() => (acting ? acting.userId : null), [acting]);

  const value: FloorCtx = { isFloor, token, actingName, actingUserId: actingUid, adopt, lock, holdLock };

  if (!isFloor) {
    // Sesión normal: contexto neutro (isFloor=false) y cero UI extra.
    return <Ctx.Provider value={{ ...value, isFloor: false }}>{children}</Ctx.Provider>;
  }

  // BLOQUEADA: SOLO el lock (los hijos ni se montan → nada se ve ni se pide).
  if (!acting) {
    const brand = me?.tenant?.branding ?? {};
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-slate-100">
        <div className="flex w-full max-w-xs flex-col items-center gap-4 px-4">
          {brand.brandLogoUrl
            ? <img src={brand.brandLogoUrl} alt={brand.brandName ?? ''} className="max-h-16 object-contain" />
            : <p className="font-serif text-2xl text-slate-900">{brand.brandName ?? me?.tenant?.name ?? 'FluidSol'}</p>}
          <FloorPinModal title="Pantalla bloqueada" dismissable={false} onOk={adopt} onClose={() => {}} />
          {/* Paso 0 de TODO smoke: confirmar el build en pantalla (regla 11/09). */}
          <p className="text-[10px] text-slate-400">build {BUILD_ID}</p>
        </div>
      </div>
    );
  }

  return (
    <Ctx.Provider value={value}>
      {/* Barra de piso: quién opera + descanso + cambiar + bloquear (siempre visible). */}
      <div className="fixed right-3 top-2 z-40 flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs shadow-sm">
        <span className="font-semibold text-slate-800">{acting.name}</span>
        {/* P3 (descubribilidad 12/09) — el mesero sale a descanso con SU PIN, aquí
            mismo. "Salir a descanso" → registra el break y bloquea (al volver, teclea
            su PIN). El admin conserva su camino en Piso y PINs. */}
        <button className="text-amber-600 underline decoration-dotted hover:text-amber-800"
          onClick={async () => {
            try { await api('/pos/floor/break', { method: 'PUT', body: { userId: acting.userId, onBreak: true } }); }
            catch { /* el lock igual protege; el estado real se lee en Piso y PINs */ }
            lock();
          }}
          title="Registra tu descanso y bloquea; al volver teclea tu PIN">Salir a descanso</button>
        <button className="text-slate-500 underline decoration-dotted hover:text-slate-900"
          onClick={lock} title="Otro mesero teclea su PIN">Cambiar mesero</button>
        <button className="rounded-full bg-slate-900 px-2.5 py-0.5 font-medium text-white hover:bg-slate-700"
          onClick={lock}>🔒 Bloquear</button>
      </div>
      {children}
    </Ctx.Provider>
  );
}
